import { useState } from "react";
import { ShoppingBag } from "lucide-react";
import { getCatalogueIcon, getGenreIcon } from "@/features/shop/config/catalogueIcons.config";
import type { AutocompleteResult } from "@/features/shop/types/autocomplete.types";

type AutocompleteItemProps = {
  result: AutocompleteResult
  isHighlighted: boolean
  onSelect: (result: AutocompleteResult) => void
  onMouseEnter: () => void
};

export const AutocompleteItem = ({
  result,
  isHighlighted,
  onSelect,
  onMouseEnter,
}: AutocompleteItemProps) => {
  const genreIcon = result.genre ? getGenreIcon(result.genre) : ShoppingBag;
  const Icon = genreIcon !== ShoppingBag ? genreIcon : getCatalogueIcon(result.catalogue);

  const buttonClassName = isHighlighted
    ? "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 focus:outline-none bg-primary-50 dark:bg-primary-900/20"
    : "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 focus:outline-none hover:bg-gray-50 dark:hover:bg-gray-700/50";

  const iconContainerClassName = isHighlighted
    ? "flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 dark:bg-primary-800/30 dark:text-primary-400"
    : "flex size-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400";

  const textClassName = isHighlighted
    ? "text-sm font-medium truncate text-primary-700 dark:text-primary-300"
    : "text-sm font-medium truncate text-gray-900 dark:text-white";

  const subtitleClassName = isHighlighted
    ? "text-xs truncate text-primary-500 dark:text-primary-400"
    : "text-xs truncate text-gray-500 dark:text-gray-400";

  const subtitle = [result.genre, result.department].filter(Boolean).join(" · ");

  // Vignette de l'événement (billetterie) ; l'icône reste en repli si elle manque ou échoue.
  // L'échec est mémorisé par URL : une nouvelle URL pour le même résultat est retentée.
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const showThumbnail = Boolean(result.image_url) && failedImageUrl !== result.image_url;

  // Même emplacement de 36 px que la tuile d'icône : le texte reste aligné d'une ligne à l'autre
  const thumbnailClassName = isHighlighted
    ? "h-9 w-7 rounded-lg bg-gray-100 object-cover ring-2 ring-primary-300 dark:bg-gray-700 dark:ring-primary-600"
    : "h-9 w-7 rounded-lg bg-gray-100 object-cover dark:bg-gray-700";

  return (
    <button
      type="button"
      onClick={() => onSelect(result)}
      onMouseEnter={onMouseEnter}
      className={buttonClassName}
    >
      {showThumbnail
        ? (
          <div className="flex size-9 shrink-0 items-center justify-center">
            <img
              src={result.image_url ?? undefined}
              alt=""
              loading="lazy"
              data-testid="autocomplete-thumbnail"
              className={thumbnailClassName}
              onError={() => setFailedImageUrl(result.image_url ?? null)}
            />
          </div>
        )
        : (
          <div className={iconContainerClassName} data-testid="autocomplete-icon">
            <Icon className="size-5" strokeWidth={2} />
          </div>
        )}
      <div className="flex flex-col min-w-0">
        <span className={textClassName}>
          {result.name}
        </span>
        {subtitle && (
          <span className={subtitleClassName}>
            {subtitle}
          </span>
        )}
      </div>
    </button>
  );
};

AutocompleteItem.displayName = "AutocompleteItem";
